/**
 * AI响应诊断工具
 * 用于分析和诊断AI返回的JSON内容问题
 */
export class AIResponseDiagnostic {
  
  /**
   * 诊断AI响应
   * @param {string} response - AI返回的原始响应
   * @returns {Object} 诊断结果
   */
  static diagnoseResponse(response) {
    console.log('🔍 开始诊断AI响应...')
    
    const result = {
      originalLength: response.length,
      hasJsonStart: false,
      hasJsonEnd: false,
      jsonStartPosition: -1,
      jsonEndPosition: -1,
      extractedJson: '',
      jsonErrors: [],
      suggestions: [],
      rawResponse: response
    }
    
    // 1. 检查是否有JSON开始标记
    const jsonStartMatch = response.match(/\{/)
    if (jsonStartMatch) {
      result.hasJsonStart = true
      result.jsonStartPosition = jsonStartMatch.index
      console.log('✅ 找到JSON开始位置:', result.jsonStartPosition)
    } else {
      result.jsonErrors.push('未找到JSON开始标记 {')
      result.suggestions.push('AI可能没有返回JSON格式的数据')
    }
    
    // 2. 检查是否有JSON结束标记
    const jsonEndMatch = response.match(/\}/)
    if (jsonEndMatch) {
      result.hasJsonEnd = true
      result.jsonEndPosition = jsonEndMatch.index
      console.log('✅ 找到JSON结束位置:', result.jsonEndPosition)
    } else {
      result.jsonErrors.push('未找到JSON结束标记 }')
      result.suggestions.push('AI返回的JSON可能不完整')
    }
    
    // 3. 提取JSON内容
    if (result.hasJsonStart && result.hasJsonEnd) {
      result.extractedJson = response.substring(result.jsonStartPosition, result.jsonEndPosition + 1)
      console.log('📄 提取的JSON长度:', result.extractedJson.length)
      
      // 4. 分析JSON语法
      this.analyzeJsonSyntax(result.extractedJson, result)
    }
    
    // 5. 分析原始响应
    this.analyzeRawResponse(response, result)
    
    return result
  }
  
  /**
   * 分析JSON语法
   */
  static analyzeJsonSyntax(jsonString, result) {
    try {
      JSON.parse(jsonString)
      console.log('✅ JSON语法正确')
    } catch (error) {
      result.jsonErrors.push(`JSON语法错误: ${error.message}`)
      
      // 分析具体错误
      this.analyzeJsonError(error, jsonString, result)
    }
  }
  
  /**
   * 分析JSON错误
   */
  static analyzeJsonError(error, jsonString, result) {
    const errorMessage = error.message
    
    if (errorMessage.includes('Expected property name')) {
      result.suggestions.push('JSON中可能有未转义的引号或特殊字符')
      this.findUnescapedQuotes(jsonString, result)
    }
    
    if (errorMessage.includes('Unexpected token')) {
      result.suggestions.push('JSON中可能有无效的字符或格式')
      this.findInvalidTokens(jsonString, result)
    }
    
    if (errorMessage.includes('Unexpected end')) {
      result.suggestions.push('JSON可能被截断或不完整')
      this.checkJsonCompleteness(jsonString, result)
    }
    
    if (errorMessage.includes('position')) {
      const positionMatch = errorMessage.match(/position (\d+)/)
      if (positionMatch) {
        const position = parseInt(positionMatch[1])
        this.analyzePositionError(jsonString, position, result)
      }
    }
  }
  
  /**
   * 查找未转义的引号
   */
  static findUnescapedQuotes(jsonString, result) {
    const lines = jsonString.split('\n')
    lines.forEach((line, index) => {
      // 检查中文字符后的引号
      if (line.match(/[\u4e00-\u9fff]"[^,}\]]/)) {
        result.suggestions.push(`第${index + 1}行可能有未转义的中文引号`)
      }
      
      // 检查属性名后的引号
      if (line.match(/[^\\]"[^:]/)) {
        result.suggestions.push(`第${index + 1}行可能有未转义的属性值引号`)
      }
    })
  }
  
  /**
   * 查找无效的token
   */
  static findInvalidTokens(jsonString, result) {
    // 检查是否有单引号
    if (jsonString.includes("'")) {
      result.suggestions.push('JSON中使用了单引号，应该使用双引号')
    }
    
    // 检查是否有注释
    if (jsonString.includes('//') || jsonString.includes('/*')) {
      result.suggestions.push('JSON中包含了注释，需要移除')
    }
    
    // 检查是否有尾随逗号
    if (jsonString.match(/,(\s*[}\]])/)) {
      result.suggestions.push('JSON中有尾随逗号，需要移除')
    }
  }
  
  /**
   * 检查JSON完整性
   */
  static checkJsonCompleteness(jsonString, result) {
    const openBraces = (jsonString.match(/\{/g) || []).length
    const closeBraces = (jsonString.match(/\}/g) || []).length
    const openBrackets = (jsonString.match(/\[/g) || []).length
    const closeBrackets = (jsonString.match(/\]/g) || []).length
    
    if (openBraces !== closeBraces) {
      result.suggestions.push(`大括号不匹配: 开始${openBraces}个，结束${closeBraces}个`)
    }
    
    if (openBrackets !== closeBrackets) {
      result.suggestions.push(`方括号不匹配: 开始${openBrackets}个，结束${closeBrackets}个`)
    }
  }
  
  /**
   * 分析位置错误
   */
  static analyzePositionError(jsonString, position, result) {
    const lines = jsonString.substring(0, position).split('\n')
    const lineNumber = lines.length
    const columnNumber = lines[lines.length - 1].length + 1
    
    result.suggestions.push(`错误位置: 第${lineNumber}行第${columnNumber}列`)
    
    // 显示错误位置的上下文
    const contextStart = Math.max(0, position - 50)
    const contextEnd = Math.min(jsonString.length, position + 50)
    const context = jsonString.substring(contextStart, contextEnd)
    
    result.suggestions.push(`错误上下文: ...${context}...`)
  }
  
  /**
   * 分析原始响应
   */
  static analyzeRawResponse(response, result) {
    // 检查响应是否包含非JSON内容
    const lines = response.split('\n')
    const nonJsonLines = lines.filter(line => 
      !line.trim().startsWith('{') && 
      !line.trim().startsWith('}') && 
      !line.trim().startsWith('"') &&
      !line.trim().startsWith('[') &&
      !line.trim().startsWith(']') &&
      !line.trim().startsWith(',') &&
      !line.trim().startsWith(':') &&
      line.trim() !== ''
    )
    
    if (nonJsonLines.length > 0) {
      result.suggestions.push(`响应中包含${nonJsonLines.length}行非JSON内容`)
      result.suggestions.push('建议在提示词中明确要求只返回JSON')
    }
    
    // 检查响应长度
    if (response.length > 10000) {
      result.suggestions.push('响应过长，可能被截断')
    }
    
    // 检查是否包含错误信息
    if (response.includes('error') || response.includes('Error')) {
      result.suggestions.push('响应中可能包含错误信息')
    }
  }
  
  /**
   * 生成修复建议
   */
  static generateFixSuggestions(diagnostic) {
    const suggestions = []
    
    if (diagnostic.jsonErrors.length > 0) {
      suggestions.push('🔧 JSON修复建议:')
      diagnostic.jsonErrors.forEach(error => {
        suggestions.push(`  - ${error}`)
      })
    }
    
    if (diagnostic.suggestions.length > 0) {
      suggestions.push('💡 提示词优化建议:')
      diagnostic.suggestions.forEach(suggestion => {
        suggestions.push(`  - ${suggestion}`)
      })
    }
    
    return suggestions
  }
  
  /**
   * 生成诊断报告
   */
  static generateReport(diagnostic) {
    const report = {
      summary: {
        hasValidJson: diagnostic.jsonErrors.length === 0,
        totalErrors: diagnostic.jsonErrors.length,
        totalSuggestions: diagnostic.suggestions.length,
        responseLength: diagnostic.originalLength
      },
      errors: diagnostic.jsonErrors,
      suggestions: diagnostic.suggestions,
      fixSuggestions: this.generateFixSuggestions(diagnostic)
    }
    
    return report
  }
}
